import type { Card, CardColor, CardKind } from '../cards.js'
import type { LocaleTexts, LogMessage, NameOf } from '../i18n.js'
import type { Army, GameEndReason } from '../state.js'

/** Spanish texts produced by the server (game log, errors). */

/** Plural of a counted word, Spanish rule (only 1 is singular). */
function n(count: number, word: string, plural = `${word}s`): string {
  return `${count} ${count === 1 ? word : plural}`
}

const COLORS: Record<CardColor, string> = { red: 'Rojo', blue: 'Azul', green: 'Verde', yellow: 'Amarillo' }
/** Feminine forms, for the cards whose name is feminine ("Inversión Roja"). */
const COLORS_FEM: Record<CardColor, string> = { red: 'Roja', blue: 'Azul', green: 'Verde', yellow: 'Amarilla' }

const KINDS: Record<CardKind, string> = {
  number: 'Número',
  zero: '0',
  skip: 'Pierde turno',
  reverse: 'Inversión',
  plus2: '+2',
  wild: 'Joker',
  wild4: '+4',
}

const WIN_REASONS: Partial<Record<GameEndReason, string>> = {
  king_captured: 'captura del rey rival',
  checkmate: 'jaque mate',
  resign: 'abandono del rival',
  abandon: 'incomparecencia del rival (desconexión prolongada o inactividad)',
}

function cardLabel(card: Card): string {
  const color = card.color ? COLORS[card.color] : ''
  switch (card.kind) {
    case 'number':
      return `${card.value} ${color}`
    case 'zero':
      return `0 ${color}`
    case 'skip':
    case 'plus2':
      return `${KINDS[card.kind]} ${color}`
    case 'reverse':
      return `${KINDS.reverse} ${card.color ? COLORS_FEM[card.color] : ''}`
    case 'wild':
    case 'wild4':
      return KINDS[card.kind]
  }
}

function log(m: LogMessage, name: NameOf, card: (cardId: string) => string): string {
  const army = (a: Army) => (a === 'w' ? 'las blancas' : 'las negras')
  switch (m.id) {
    case 'game_start':
      return `Empieza la partida: ${name(m.white)} juega con las blancas, ${name(m.black)} con las negras.`
    case 'deck_reshuffled':
      return `El descarte se baraja para formar un nuevo mazo (${n(m.count, 'carta')}).`
    case 'deck_empty':
      return `${name(m.player)} no puede robar: no queda ninguna carta.`
    case 'card_drawn':
      return `${name(m.player)} roba y revela ${card(m.cardId)}.`
    case 'turn_start':
      return `Turno ${m.turn}: ${name(m.player)} juega con ${army(m.army)}.`
    case 'number_card':
      return `${name(m.player)} juega ${n(m.moves, 'movimiento')} este turno.`
    case 'zero_card':
      return `0: ${name(m.player)} pasa el turno sin mover.`
    case 'skip_card':
      return `Pierde turno: ${name(m.player)} pierde el turno, ${name(m.next)} roba y juega.`
    case 'skip_in_check':
      return `Pierde turno: ${name(m.player)} está en jaque y no puede perder el turno; juega 1 movimiento para defenderse.`
    case 'recovery_in_check':
      return `${name(m.player)} sigue en jaque: juega 1 movimiento para defenderse.`
    case 'no_pawn_to_recover':
      return `${card(m.cardId)}: ningún peón que recuperar para ${army(m.army)}${m.noSquare ? ' (ninguna casilla libre)' : ''}.`
    case 'pawns_recovered':
      return `${card(m.cardId)}: ${name(m.player)} recupera ${n(m.count, 'peón', 'peones')} para volver a colocar.`
    case 'reverse_card':
      return `Inversión: ${name(m.white)} controla ahora las blancas y ${name(m.black)} las negras. ${name(m.player)} juega con ${army(m.army)}.`
    case 'joker_card':
      return `Joker: ${name(m.player)} puede mover cualquier pieza como una dama (1 movimiento).`
    case 'effect_chosen':
      return `${name(m.player)} elige ${n(m.moves, 'movimiento')}.`
    case 'pawn_placed':
      return `${name(m.player)} vuelve a colocar un peón en ${m.square}.`
    case 'no_move_turn_ends':
      return `${name(m.player)} no tiene ningún movimiento posible: fin del turno.`
    case 'turn_end':
      return `Fin del turno ${m.turn}.`
    case 'draw_agreed':
      return 'Tablas: ambos jugadores han aceptado las tablas.'
    case 'draw_no_moves':
      return `Tablas: ${m.turns} turnos seguidos sin que se mueva ninguna pieza.`
    case 'win':
      return `${name(m.winner)} gana por ${WIN_REASONS[m.reason] ?? m.reason}.`
    case 'move':
      return `${name(m.player)} (${army(m.army)}) juega ${m.san}.`
    case 'series_stopped_threat':
      return 'El rey rival está atacado: la serie de movimientos se detiene.'
    case 'no_move_left':
      return `${name(m.player)} ya no tiene ningún movimiento posible.`
    case 'series_ended':
      return `${name(m.player)} termina su serie de movimientos.`
    case 'draw_offered':
      return `${name(m.player)} ofrece tablas.`
    case 'draw_declined':
      return `${name(m.player)} rechaza las tablas.`
  }
}

const errors: LocaleTexts['errors'] = {
  game_over: 'La partida ha terminado.',
  server_only: 'Comando reservado al servidor.',
  forfeit_needs_player: 'Una incomparecencia debe indicar el jugador (usar applyForfeit).',
  draw_already_offered: 'Ya hay una oferta de tablas en curso.',
  no_draw_to_accept: 'No hay ninguna oferta de tablas que aceptar.',
  no_draw_to_decline: 'No hay ninguna oferta de tablas que rechazar.',
  not_your_turn: 'No te toca jugar.',
  no_effect_pending: 'No hay ningún efecto pendiente.',
  no_effect_to_choose: 'No hay ningún efecto que elegir.',
  choose_moves: (p) => `Elige entre 1 y ${p.max} movimientos.`,
  no_pawn_to_place: 'No hay ningún peón que colocar.',
  invalid_pawn_square: 'No se puede colocar un peón en esta casilla.',
  cannot_place_pawn: 'No se puede colocar el peón aquí.',
  not_move_phase: 'No es la fase de movimientos.',
  invalid_move: 'Movimiento no permitido.',
  king_attacked_square: 'El rey no puede ir a una casilla atacada.',
  king_left_in_check: 'Este movimiento dejaría a tu rey en jaque.',
  king_not_capturable: 'En el modo Purista, el rey no se puede capturar.',
  first_move_required: 'El primer movimiento es obligatorio.',
  no_series: 'No hay ninguna serie de movimientos en curso.',
  unknown_command: 'Comando desconocido.',
  invite_unknown: 'Código de invitación desconocido.',
  game_not_found: 'Partida no encontrada.',
  game_full: 'Esta partida ya está completa.',
  not_in_game: 'No participas en esta partida.',
  game_not_started: 'La partida aún no ha empezado.',
  game_not_finished: 'La partida no ha terminado.',
  stale_command: (p) => `Comando obsoleto: versión esperada ${p.expected}, recibida ${p.received}.`,
  command_id_reused: 'Identificador de comando ya utilizado.',
  view_unavailable: 'Vista no disponible.',
  cannot_challenge_self: 'No puedes desafiarte a ti mismo.',
  player_not_found: 'Jugador no encontrado.',
  rate_limited: 'Demasiadas solicitudes, vuelve a intentarlo en un momento.',
  invalid_request: 'Solicitud no válida.',
  internal: 'Error interno del servidor.',
  server_unreachable: 'El servidor no responde.',
  username_taken: 'Este nombre de usuario ya está en uso.',
  bad_credentials: 'Nombre de usuario o contraseña incorrectos.',
  too_many_attempts: 'Demasiados intentos, vuelve a intentarlo dentro de un minuto.',
  invalid_data: 'Datos no válidos.',
  username_format: 'Nombre de usuario: de 3 a 20 letras, cifras, «_», «-» o «.».',
  password_length: 'Contraseña: 6 caracteres como mínimo.',
  credentials_required: 'Se requieren nombre de usuario y contraseña.',
  invalid_session: 'Sesión no válida.',
  match_not_ranked: 'Partida no clasificada.',
  login_failed: 'No se ha podido iniciar sesión.',
  feedback_empty: 'Escribe un mensaje o da una puntuación.',
  feedback_rate_limited: '¡Gracias! Ya has enviado varios mensajes: vuelve a intentarlo un poco más tarde.',
  tournament_unknown: 'Código de torneo desconocido.',
  tournament_started: 'Este torneo ya ha empezado.',
  tournament_full: 'Este torneo está completo.',
  tournament_not_host: 'Solo el organizador puede hacer esto.',
  tournament_not_open: 'Las inscripciones de este torneo están cerradas.',
  tournament_needs_players: 'Se necesitan al menos 2 jugadores para empezar el torneo.',
  tournament_over: 'Este torneo ha terminado.',
  tournament_too_many: 'Ya organizas demasiados torneos en curso: termina o cancela uno antes.',
  tournament_no_rematch: 'No hay revancha en un torneo: la competición continúa desde el cuadro.',
}

export const es: LocaleTexts = {
  cardColors: COLORS,
  cardKinds: KINDS,
  cardLabel,
  winReasons: WIN_REASONS,
  log,
  errors,
}
